import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
  requiredRole?: 'super_admin' | 'admin' | 'moderator' | 'data_entry' | 'viewer';
}

const ROLE_HIERARCHY = ['viewer', 'data_entry', 'moderator', 'admin', 'super_admin'];

const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ 
  children, 
  requiredRole = 'moderator'
}) => {
  const { admin, loading, refreshActivity, signOut } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (admin) refreshActivity();
  }, [location.pathname]);

  // Invalid role: properly sign out to prevent redirect loop
  useEffect(() => {
    if (!loading && admin && ROLE_HIERARCHY.indexOf(admin.role) === -1) {
      signOut();
      navigate('/admin/login', { replace: true });
    }
  }, [admin, loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!admin || !admin.token) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  const userRoleIndex = ROLE_HIERARCHY.indexOf(admin.role);
  if (userRoleIndex === -1) {
    return null;
  }

  const requiredRoleIndex = ROLE_HIERARCHY.indexOf(requiredRole);
  if (userRoleIndex < requiredRoleIndex) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v.01M12 9v3m0 0V9m9 3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2 text-foreground">Access Denied</h2>
          <p className="text-muted-foreground mb-4">
            You need <strong className="text-foreground">{requiredRole.replace('_', ' ')}</strong> role or higher to access this page.
          </p>
          <p className="text-xs text-muted-foreground">
            Current role: <span className="font-medium">{admin.role.replace('_', ' ')}</span>
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedAdminRoute;
